// Shared comment/string/char-literal recognizer for the C#-import pipeline.
// Used by both CsSanitizer (skip verbatim, so attribute-stripping never looks
// inside a comment/string) and CsTokenizer (strip/tokenize + XMLDoc capture).
// One recognizer, one truth about what counts as a comment/string — instead
// of two separate hand-rolled versions drifting apart.

/**
 * Recognizes a comment or string/char literal starting at index i in source.
 * @param {string} source
 * @param {number} i
 * @returns {{end: number, kind: 'xmlDocLine'|'lineComment'|'blockComment'|'string'|'char'}|null}
 *   end = index right after the recognized span. null if nothing recognized at i.
 */
export function scanCommentOrString(source, i) {
  const n = source.length;
  const ch = source[i];

  // XML doc comment (///) — checked before plain line comment (//)
  if (ch === '/' && source[i + 1] === '/' && source[i + 2] === '/') {
    let j = i;
    while (j < n && source[j] !== '\n') j += 1;
    return { end: j, kind: 'xmlDocLine' };
  }

  if (ch === '/' && source[i + 1] === '/') {
    let j = i;
    while (j < n && source[j] !== '\n') j += 1;
    return { end: j, kind: 'lineComment' };
  }

  if (ch === '/' && source[i + 1] === '*') {
    let j = i + 2;
    while (j < n && !(source[j] === '*' && source[j + 1] === '/')) j += 1;
    j = Math.min(j + 2, n);
    return { end: j, kind: 'blockComment' };
  }

  if (ch === '@' || ch === '$') {
    let k = i;
    while (source[k] === '@' || source[k] === '$') k += 1;
    if (source[k] === '"') {
      let j = k + 1;
      while (j < n && source[j] !== '"') {
        if (source[j] === '\\') j += 1;
        j += 1;
      }
      j += 1;
      return { end: j, kind: 'string' };
    }
    return null;
  }

  if (ch === '"') {
    let j = i + 1;
    while (j < n && source[j] !== '"') {
      if (source[j] === '\\') j += 1;
      j += 1;
    }
    j += 1;
    return { end: j, kind: 'string' };
  }

  if (ch === "'") {
    let j = i + 1;
    while (j < n && source[j] !== "'") {
      if (source[j] === '\\') j += 1;
      j += 1;
    }
    j += 1;
    return { end: j, kind: 'char' };
  }

  return null;
}
